import { currentUser } from "@/App";
import CommentCard from "@/components/CommentCard";
import { MusicContext } from "@/contexts/MusicContext";
import { postComment } from "@/services/api";
import { useComments } from "@/services/hooks";
import { ArrowUpward, AttachMoney } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useState } from "react";
import LoadingSkeleton from "./LoadingSkeleton";

export default function CommentsContainer({ content, background = "#47596E" }) {
  const queryClient = useQueryClient();
  const { data: comments, isLoading } = useComments(content?.key);
  const { currentTime } = useContext(MusicContext);

  const [text, setText] = useState("");
  const [shouldScroll, setShouldScroll] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (shouldScroll && scrollRef.current) {
      // Small delay to ensure DOM has fully updated
      setTimeout(() => {
        scrollRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        setShouldScroll(false);
      }, 100);
    }
  }, [comments?.length, shouldScroll]);

  const mutation = useMutation({
    mutationFn: (variables) =>
      postComment(variables.contentKey, variables.text, variables.currentTime),
    onMutate: async () => {
      await queryClient.setQueryData(["comments", content.key], (old) => [
        ...old,
        {
          id: Date.now(), // temporary ID until real data comes back
          content_key: content.key,
          user_key: currentUser.key,
          parent_id: null,
          type: "comment",
          data: {
            text: text,
            current_time: currentTime || undefined,
          },
          created: Math.floor(Date.now() / 1000),
          favorite_count: 0,
          user_favorite: false,
          artist_favorite: false,
          replies: [],
          user: currentUser,
        },
      ]);
      // setShouldScroll(true);
    },
    onSuccess: () => {
      setText("");
      queryClient.invalidateQueries({ queryKey: ["comments", content.key] });
    },
    onError: (error) => {
      console.error("Error:", error);
      alert("Error: " + error.message);
    },
  });

  const handleSubmit = () => {
    if (!text.trim()) return;
    mutation.mutate({
      contentKey: content.key,
      text: text,
      currentTime: currentTime,
    });
  };

  // I wanted sortedComments to be sorted by created
  const sortedComments = comments?.sort((a, b) => b.created - a.created);

  if (isLoading) {
    return (
      <Box sx={{ zIndex: 1000 }}>
        <LoadingSkeleton />
        <LoadingSkeleton />
        <LoadingSkeleton />
      </Box>
    );
  }

  return (
    <Box
      id="comments-container"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        // pt: 2,
      }}
    >
      {isLoading && (
        <Box sx={{ zIndex: 1000 }}>
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </Box>
      )}

      {!isLoading && (
        <Box
          sx={{
            overflowY: "scroll",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::WebkitScrollbar": {
              display: "none",
            },
          }}
        >
          <Box ref={scrollRef} />
          {sortedComments?.map((event, index) => (
            <Box key={index} sx={{ my: 3 }}>
              <CommentCard event={event} content={content} />
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: background,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              py: 2,
            }}
          >
            <Avatar
              src={currentUser.meta.profile}
              alt={currentUser.username}
              sx={{ width: 34, height: 34, mt: 0.5 }}
            />
            <TextField
              fullWidth
              multiline
              autoComplete="off"
              maxRows={5}
              size="small"
              placeholder="Join the conversation..."
              variant="outlined"
              value={text}
              sx={{ mx: 1 }}
              disabled={mutation.isPending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              onChange={(e) => setText(e.target.value)}
            />
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* <TipArtistButton /> */}
              <IconButton
                size="small"
                sx={{ background: "#9A5AEF", mt: 0.5 }}
                onClick={handleSubmit}
                disabled={mutation.isPending || !text.trim()}
              >
                <ArrowUpward />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

function TipArtistButton() {
  return (
    <Tooltip title="Tip Artist">
      <IconButton
        variant="outlined"
        color="secondary"
        size="small"
        sx={{ mr: 1 }}
      >
        <AttachMoney />
      </IconButton>
    </Tooltip>
  );
}
